import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import PropTypes from 'prop-types';
import { setCallExpired } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setHoveredID } from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import { formatPhoneWithDashes } from '@rsos/utils';
import { getExpiredCallAge } from '@rsos/utils/metaTags';
import { displayMilitaryTime } from '@rsos/utils/timeHelpers';
import usePortalType from '@rsos/utils/usePortalType';
import usePrevious from '@rsos/utils/usePrevious';
import { SOURCE_SMS } from '../../../../constants/locationSources';
import {
  Container,
  IconBox,
  CallBox,
  PhoneNumber,
  PhoneDetails,
  CallStatusTag,
  CallStatus,
  TextMessage,
  SiriusXMMessage,
  TimeBox,
  Time,
  RemoveCall,
} from './EmergencyCall.styles';
import EmergencyCallIcons from './EmergencyCallIcons';
import { getCallStatusName } from './helpers';

const expiredCallAge = getExpiredCallAge();

const EmergencyCall = ({
  applicationType,
  classOfService,
  callerID,
  callStartTime,
  callStatus,
  emergencyCallID,
  hasAgent511Access,
  hasGoogleCarCrashData,
  hasGoogleCallerInfo,
  isApple,
  isConnectedCar,
  isCallerIn911informBuilding,
  isCallExpired,
  isCallReceived,
  isCallShared,
  isInternational,
  isManualSearch,
  isMapMarkerHovered,
  isOnsitePersonnelNotified,
  isRemoveCallVisible,
  isSearchResult,
  isSearchResultHovered,
  isSelected,
  isTT911,
  isUnread,
  isUnseen,
  onRemoveCall,
  setCallerID,
  source,
  tt911ADR,
}) => {
  const dispatch = useDispatch();
  const previousCallStartTime = usePrevious(callStartTime);
  const { isPremiumUser } = usePortalType();
  const isSMS = source === SOURCE_SMS || isTT911;
  const tt911Message = tt911ADR?.data?.message;
  const [googleIndicator, setGoogleIndicator] = useState(null);
  const [displayRemoveCall, setDisplayRemoveCall] = useState(
    isRemoveCallVisible,
  );
  const callStatusDisplayName = getCallStatusName(callStatus);

  const animateProps = useSpring({
    config: { duration: 200 },
    display: displayRemoveCall ? 'flex' : 'none',
    opacity: displayRemoveCall ? 1 : 0,
  });

  const checkCallExpired = useCallback(() => {
    const currentTime = new Date().getTime();
    return (currentTime - callStartTime) / 1000 > expiredCallAge;
  }, [callStartTime]);

  const timeExpired = () => {
    const currentTime = new Date().getTime();
    const seconds = (currentTime - callStartTime) / 1000;
    let d = Math.floor(seconds / (3600 * 24));
    let h = Math.floor((seconds % (3600 * 24)) / 3600);

    let dDisplay = d > 0 ? d + (d == 1 ? ' day' : ' days') : '';
    let hDisplay = h > 0 ? h + (h == 1 ? ' hour' : ' hours') : '';
    return dDisplay || hDisplay ? `${dDisplay} ${hDisplay} old` : '';
  };

  // check if call expired on load
  useEffect(() => {
    if (!isCallExpired && checkCallExpired()) {
      dispatch(
        setCallExpired({
          callerID,
          isCallExpired: true,
        }),
      );
    }
  }, [dispatch, callerID, checkCallExpired, isCallExpired]);

  // if call expired but call start time changed, set it to not expired
  useEffect(() => {
    if (
      isCallExpired &&
      previousCallStartTime &&
      callStartTime !== previousCallStartTime
    ) {
      dispatch(
        setCallExpired({
          callerID,
          isCallExpired: false,
        }),
      );
    }
  }, [dispatch, callerID, callStartTime, isCallExpired, previousCallStartTime]);

  // set call expired based on 2 hours (expiredCallAge) past call start time,
  useEffect(() => {
    const callExpiredTimer =
      new Date(callStartTime + expiredCallAge * 1000) - new Date();

    const timer = setTimeout(() => {
      if (!isCallExpired && checkCallExpired()) {
        dispatch(
          setCallExpired({
            callerID,
            isCallExpired: true,
          }),
        );
      }
    }, callExpiredTimer);

    return () => clearTimeout(timer);
  }, [dispatch, callerID, callStartTime, checkCallExpired, isCallExpired]);

  useEffect(() => {
    emergencyCallID === callerID
      ? setDisplayRemoveCall(true)
      : setDisplayRemoveCall(false);
  }, [emergencyCallID, callerID]);

  useEffect(() => {
    if (hasGoogleCallerInfo && hasGoogleCarCrashData) {
      setGoogleIndicator('Language, Crash Detection Sensor');
    } else if (hasGoogleCallerInfo) {
      setGoogleIndicator('Language');
    } else if (hasGoogleCarCrashData) {
      setGoogleIndicator('Crash Detection Sensor');
    }
  }, [hasGoogleCallerInfo, hasGoogleCarCrashData]);

  const handleRemoveCall = e => {
    e.stopPropagation();
    onRemoveCall({
      caller_id: emergencyCallID,
      call_start_time: callStartTime,
    });
    setDisplayRemoveCall(false);
  };

  return (
    <Container
      isExpired={isCallExpired}
      isHovered={isMapMarkerHovered || isSearchResultHovered}
      isSearchResult={isSearchResult}
      isSelected={isSelected}
      isUnread={isUnread}
      isUnseen={isUnseen}
      onClick={() => {
        setCallerID(callerID);
        dispatch(setHoveredID(null));
      }}
    >
      <IconBox>
        <EmergencyCallIcons
          isManualSearch={isManualSearch}
          isCallReceived={isCallReceived}
          isCallShared={isCallShared}
          isSMS={isSMS}
          isSMSReceived={isSMS && isCallReceived}
          isSMSShared={isSMS && isCallShared}
          isUnseen={isUnseen}
          applicationType={applicationType}
          classOfService={classOfService}
          isPremiumUser={isPremiumUser}
        />
      </IconBox>
      <CallBox>
        <PhoneNumber isUnseen={isUnseen}>
          {isInternational ? callerID : formatPhoneWithDashes(callerID)}
        </PhoneNumber>
        {callStatusDisplayName && (
          <CallStatusTag status={callStatus} isCallPopup={false}>
            <CallStatus status={callStatus}>{callStatusDisplayName}</CallStatus>
          </CallStatusTag>
        )}
        {isSMS &&
          (hasAgent511Access && tt911Message ? (
            <TextMessage>{tt911Message}</TextMessage>
          ) : (
            <PhoneDetails>Text-to-911</PhoneDetails>
          ))}
        {isConnectedCar && (
          <SiriusXMMessage>Veh. Crash Detected</SiriusXMMessage>
        )}
        {isApple && <PhoneDetails>Add'l Data Avail.</PhoneDetails>}
        {googleIndicator && <PhoneDetails>{googleIndicator}</PhoneDetails>}
        {isCallerIn911informBuilding && (
          <PhoneDetails>Connected Building</PhoneDetails>
        )}
        {/* Note: it's unlikely for there to be text-to-911, appple data, and
          sxm data all at once. if more needs to be added here, need to prioritize
          and potentially show "more" if there are space constraints*/}
        {isCallExpired && (
          <>
            <PhoneDetails>{timeExpired()}</PhoneDetails>
            <PhoneDetails>Right click to remove</PhoneDetails>
          </>
        )}
        {isOnsitePersonnelNotified && (
          <PhoneDetails>Onsite Personnel Notified</PhoneDetails>
        )}
      </CallBox>
      {callStartTime && (
        <TimeBox>
          <Time>{displayMilitaryTime(callStartTime)}</Time>
        </TimeBox>
      )}

      <animated.div style={animateProps}>
        <RemoveCall onClick={handleRemoveCall}>Remove</RemoveCall>
      </animated.div>
    </Container>
  );
};

EmergencyCall.propTypes = {
  applicationType: PropTypes.string,
  callerID: PropTypes.string,
  callStartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  callStatus: PropTypes.string,
  emergencyCallID: PropTypes.string,
  hasAgent511Access: PropTypes.bool,
  hasGoogleCarCrashData: PropTypes.bool,
  hasGoogleCallerInfo: PropTypes.bool,
  isApple: PropTypes.bool,
  isCallerIn911informBuilding: PropTypes.bool,
  isCallExpired: PropTypes.bool,
  isCallReceived: PropTypes.bool,
  isCallShared: PropTypes.bool,
  isConnectedCar: PropTypes.bool,
  isInternational: PropTypes.bool,
  isManualSearch: PropTypes.bool,
  isMapMarkerHovered: PropTypes.bool,
  isRemoveCallVisible: PropTypes.bool,
  isSearchResult: PropTypes.bool,
  isSearchResultHovered: PropTypes.bool,
  isSelected: PropTypes.bool,
  isTT911: PropTypes.bool,
  isUnread: PropTypes.bool,
  isUnseen: PropTypes.bool,
  onRemoveCall: PropTypes.func,
  setCallerID: PropTypes.func.isRequired,
  source: PropTypes.string,
  tt911ADR: PropTypes.object,
  isOnsitePersonnelNotified: PropTypes.bool,
};

export default EmergencyCall;
